'use client';

import { usePathname } from 'next/navigation';
import { Burger, Container, Title, Button } from '@mantine/core';
import classes from './Navbar.module.css';
import Image from 'next/image';
import Link from 'next/link';
import NotificationDropdown from './NotificationDropdown';
import UserMenu from './UserMenu';
import { useEffect, useState } from 'react';
import { useCurrentUser, useCurrentMentor } from '@/providers/CurrentUserProvider';
import { publicImageUrl } from '@/lib/staticAssets';

export default function Navbar() {
  const { user } = useCurrentUser();
  const { mentor } = useCurrentMentor();
  const pathname = usePathname();
  const [hamburgerOpened, setHamburgerOpened] = useState(false);
  const [notificationOpened, setNotificationOpened] = useState(false);
  const [userMenuOpened, setUserMenuOpened] = useState(false);

  const toggleHamburger = () => {
    setHamburgerOpened(!hamburgerOpened);
    setNotificationOpened(false);
    setUserMenuOpened(false);
  };

  const toggleNotification = () => {
    setNotificationOpened(!notificationOpened);
    setHamburgerOpened(false);
    setUserMenuOpened(false);
  };

  const toggleUserMenu = () => {
    setUserMenuOpened(!userMenuOpened);
    setHamburgerOpened(false);
    setNotificationOpened(false);
  };

  const closeAll = () => {
    setHamburgerOpened(false);
    setNotificationOpened(false);
    setUserMenuOpened(false);
  };
  

  const links = [
    {
      link: '/mentors',
      label: 'Find a mentor',
      isActive: () => pathname === '/mentors'
    },
  ];
  if (user) {
    links.push({
      link: '/messages',
      label: 'Messages',
      isActive: () => !!pathname.match(/^\/(learning|mentoring)\/messages/)
    });
    links.push({
      link: '/calendar',
      label: 'Calendar',
      isActive: () => pathname === '/calendar'
    });
  }

  const items = links.map((link) => (
    <Link
      key={link.label}
      href={link.link}
      className={`${classes.link} ${link.isActive() ? classes.active : ''}`}
      onClick={closeAll}
    >
      {link.label}
    </Link>
  ));

  // Add sign-in/sign-up links to mobile menu when user is not logged in
  const mobileAuthItems = !user ? [
    <Link
      key="sign-up"
      href="/auth/sign-up"
      className={`${classes.link} ${classes.mobileAuthItem}`}
      onClick={closeAll}
    >
      Sign Up
    </Link>,
    <Link
      key="sign-in"
      href="/auth/sign-in"
      className={`${classes.link} ${classes.mobileAuthItem}`}
      onClick={closeAll}
    >
      Sign In
    </Link>
  ] : [];

  // Show mentor profile CTA using same logic as UserMenu
  const showMentorProfileCta = user?.isMentorApproved && !mentor?.handle;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        document.body.classList.add("scrolled");
      } else {
        document.body.classList.remove("scrolled");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <header 
        className={classes.header}
        style={{
          backgroundColor: 'var(--mantine-color-gray-0)'
        }}
      >
        <Container size="xl" className={classes.inner}>
          <div className={classes.navWrapper}>
            <Title order={2} className={classes.logo}>
              <Link
                href="/"
                style={{
                  textDecoration: 'none',
                  color: 'var(--mantine-primary-color-9)',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Image
                  src={publicImageUrl('images/mentorsub-logo.svg')}
                  alt="MentorSub"
                  title="MentorSub"
                  width={170}
                  height={52}
                  priority
                  style={{
                    width: '170px',
                    height: '52px',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              </Link>
            </Title>

            <nav
              className={`${classes.links} ${
                hamburgerOpened ? classes.mobileMenuOpened : classes.mobileMenuClosed
              }`}
            >
              {items}
              {mobileAuthItems}
            </nav>
          </div>

                    <div className={classes.rightSection}>
            {showMentorProfileCta && (
              <Button
                component={Link}
                href="/account/profile?mentor_profile=true"
                variant="filled"
                size="sm"
                style={{ marginRight: '1rem' }}
                visibleFrom="lg"
              >
                Create Mentor Profile
              </Button>
            )}
            {user && <NotificationDropdown isOpen={notificationOpened} onToggle={toggleNotification} />}
            <UserMenu isOpen={userMenuOpened} onToggle={toggleUserMenu} />
            <Burger 
              opened={hamburgerOpened} 
              onClick={toggleHamburger} 
              className={classes.burger} 
              size={24}
              aria-label={hamburgerOpened ? "Close navigation menu" : "Open navigation menu"}
            />
          </div>
        </Container>
      </header>
    </>
  );
}
